import { Controller, Control, RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MarketField, MarketInputPassword } from '@market/react';
import { HTMLAttributes } from 'react';

export const PASSWORD_INPUT_TEXT_TEST_ID = 'pasword-input-text-test-id';

interface ControlledInputPasswordProps extends HTMLAttributes<HTMLElement> {
  // fields used by Controller
  name: string;
  control: Control;
  defaultValue?: string;
  rules?: RegisterOptions;
  shouldUnregister?: boolean;

  // fields used by Market
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  maxlength?: number;
  minlength?: number;
  onClick?: () => void;

  // the rules prop is not used if a custom resolver is provided
  // this will be set as false by default as most of our controlled inputs will be used in forms
  // if you are using this component outside of a form, set this to true to use the rules prop
  useRules?: boolean;
}

const ControlledInputPassword = ({
  className,
  name,
  control,
  defaultValue = '',
  rules = {},
  shouldUnregister = false,
  disabled = false,
  label,
  placeholder,
  maxlength,
  minlength,
  onClick = undefined,
  useRules = false,
}: ControlledInputPasswordProps) => {
  const { i18n, t } = useTranslation();

  if (useRules) {
    // small hack because `required` doesn't doesn't do a whitespace check
    if (rules?.required) {
      // eslint-disable-next-line no-param-reassign
      rules.validate = (value) => Boolean(value.trim());
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      {...(useRules && { rules })}
      shouldUnregister={shouldUnregister}
      render={({
        field: { value, onBlur, onChange },
        fieldState: { invalid, error },
        formState: { isSubmitting },
      }) => (
        <MarketField
          className={className}
          disabled={disabled || isSubmitting}
          name={name}
          invalid={invalid}
        >
          <MarketInputPassword
            onBlur={onBlur}
            onClick={onClick}
            name={name}
            data-testid={PASSWORD_INPUT_TEXT_TEST_ID}
            value={value}
            placeholder={placeholder}
            maxlength={maxlength}
            minlength={minlength}
            // onMarketInputValueChange not defined on MarketInputPassword directly
            // even though event still bubbles up. Ts-ignore while fix is being worked on
            // https://block.atlassian.net/browse/UI-6002
            /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
            // @ts-ignore
            onMarketInputValueChange={(e) => onChange(e.detail.value)}
          >
            {label && <label>{label}</label>}
          </MarketInputPassword>
          <small slot="error">
            {error?.message && i18n.exists(error.message)
              ? t(error.message, { label: label?.toLowerCase() })
              : t('error.validation.invalidValue')}
          </small>
        </MarketField>
      )}
    />
  );
};

export default ControlledInputPassword;
